import React, { useState, useEffect } from "react";
import { PageLayout } from "../ui-components/PageLayout";
import { PieChartWrapper } from "../ui-components/Charts/PieChartWrapper";
import { BarChartWrapper } from "../ui-components/Charts/BarChartWrapper";
import {
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Fab,
  Badge,
  Tab,
  Tabs,
  Grid2,
  Checkbox,
  ListItemText,
  Alert,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { LineChartWrapper } from "../ui-components/Charts/LineChartWrapper";
import AllVariablesTrendChart from "../ui-components/Charts/AllVariablesTrendChart";
import AggregateTrendChart from "../ui-components/Charts/AggregateTrendChart";
import { RootState } from "../store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { getCategoryDataSummary } from "../store/modules/analytics/analyticsActions";
import { fetchTransactionYears } from "../store/modules/transactionYear/transactionYearActions";
import { AppDispatch } from "../store/store";
import LoadingDialog from "../ui-components/Dialogs/LoadingDialog";
import { YearFilterSelect } from "../ui-components/YearFilterSelect";
import { TransactionTypeFilterSelect } from "../ui-components/TransactionTypeFilterSelect";
import { useQuery } from "@tanstack/react-query";
import { AggregationTypesEnum } from "../types/types";
import { CoAgentFilterSelect } from "../ui-components/CoAgentFilterSelect";
import { usePageCoAgentFilter } from "../hooks/usePageCoAgentFilter";

const categoryColumns = [
  "total",
  // "average",
  "type",
  "source",
  "reason",
  // "area",
  "coAgent",
  "specialGroups",
  "specialTags",
  "zipCode",
  "county",
  "customArea",
  "vendors",
] as const;

const categoryLabels = {
  total: "Total S&B",
  // average: "Average Price S&B",
  type: "Type",
  source: "Source",
  reason: "Reason",
  // area: "Area",
  coAgent: "Team Member/Co-Agent",
  specialGroups: "Special Groups",
  specialTags: "Special Tags",
  zipCode: "Zip Code",
  county: "County",
  customArea: "Custom Area",
  vendors: "Vendors",
} as const satisfies Record<(typeof categoryColumns)[number], string>;

const Reports = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const coAgents = userData?.coAgents || [];

  // Page-specific co-agent filter
  const [effectiveCoAgentIds, setPageCoAgentIds, isGlobalFilterActive] =
    usePageCoAgentFilter("analyticsReports", selectedCoAgentIds);

  const visibleCategoryColumns = [
    // "total",
    // "average",
    ...categoryColumns.filter(
      (column) => !hiddenFields.find((field) => field.name === column),
    ),
  ] as const;
  const [selectedCategory, setSelectedCategory] = useState<
    (typeof categoryColumns)[number]
  >(categoryColumns[0] as (typeof categoryColumns)[number]);
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const transactionTypes = useSelector(
    (state: RootState) => state.commonData.userData.data?.transactionTypes,
  );
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  const [selectedTransactionTypeIds, setSelectedTransactionTypeIds] = useState<
    number[]
  >([]);

  useQuery({
    queryKey: ["fetchTransactionYears", selectedCoAgentIds],
    queryFn: () => {
      dispatch(fetchTransactionYears());
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  const selectedYearText =
    selectedYearIds.length > 0
      ? selectedYearIds
          .map((id) => years.find((y) => y.id === id)?.year)
          .join(", ")
      : "All Years";

  const [selectedVariable, setSelectedVariable] = useState<{
    id: number;
    value: string;
    color: string;
  } | null>(null);
  const [mode, setMode] = useState<"count" | "amount" | "average">("count");
  const [timePeriod, setTimePeriod] = useState<"quarter" | "month" | "year">(
    "year",
  );

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { loading } = useSelector((state: RootState) => state.analytics);

  // Add state for chart expand triggers
  const [expandPieChart, setExpandPieChart] = useState(false);
  const [expandLineChart, setExpandLineChart] = useState(false);
  const [expandAllVariablesChart, setExpandAllVariablesChart] = useState(false);

  // Reset expand triggers after they're used
  useEffect(() => {
    if (expandLineChart) {
      setTimeout(() => setExpandLineChart(false), 100);
    }
  }, [expandLineChart]);

  useEffect(() => {
    if (expandAllVariablesChart) {
      setTimeout(() => setExpandAllVariablesChart(false), 100);
    }
  }, [expandAllVariablesChart]);

  useEffect(() => {
    if (expandPieChart) {
      setTimeout(() => setExpandPieChart(false), 100);
    }
  }, [expandPieChart]);

  const handleSliceClick = (
    variableId: number,
    value: string,
    color: string,
  ) => {
    setSelectedVariable({
      id: variableId,
      value: value,
      color: color,
    });
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: (typeof categoryColumns)[number],
  ) => {
    setSelectedCategory(newValue);
    // if (newValue === "average") {
    //   setMode("amount");
    // }
    setSelectedVariable(null);
  };

  // Filter content component to reuse in both sidebar and drawer
  const FilterContent = (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        color="primary"
        component="h2"
        fontWeight="bold"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Filters
      </Typography>

      <Box sx={{ mb: 4 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel shrink={true}>Year</InputLabel>

          <Select
            multiple
            notched={true}
            value={selectedYearIds}
            displayEmpty
            onChange={(e) => {
              const value = e.target.value as number[];
              setSelectedYearIds(value);
              if (value.length === 1) {
                setTimePeriod("quarter");
              }
            }}
            label="Year"
            renderValue={(selected) => {
              if (selected.length === 0) {
                return "All Years";
              }

              if (selected.length > 2) {
                return `${selected.length} years selected`;
              }

              return selected
                .map((id) => years.find((y) => y.id === id)?.year)
                .join(", ");
            }}
            sx={{
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#1976d2",
                },
              },
            }}
            MenuProps={{
              sx: { zIndex: 1500 },
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {years.map((year) => (
              <MenuItem key={year.id} value={year.id}>
                <Checkbox
                  checked={selectedYearIds.indexOf(year.id as number) > -1}
                />
                <ListItemText primary={year.year} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <TransactionTypeFilterSelect
            transactionTypes={transactionTypes || []}
            selectedTransactionTypeIds={selectedTransactionTypeIds}
            onChange={(value) => {
              setSelectedTransactionTypeIds(value);
            }}
            label="Transaction Type"
            sx={{
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#1976d2",
                },
              },
            }}
          />
        </FormControl>

        {/* Page-level Co-Agent Filter (hidden when global filter is active or coAgent category is hidden) */}
        {!isGlobalFilterActive &&
          !hiddenFields?.some((field) => field.name === "coAgent") && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <CoAgentFilterSelect
                coAgents={coAgents}
                selectedCoAgentIds={effectiveCoAgentIds}
                onChange={setPageCoAgentIds}
                label="Filter by Team Members"
                size="medium"
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              />
            </FormControl>
          )}

        {/* {selectedCategory !== "average" && ( */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
            Data Analyzed
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, newMode) => newMode && setMode(newMode)}
            fullWidth
            size="small"
          >
            <ToggleButton value="count">Units</ToggleButton>
            <ToggleButton value="amount">Volume</ToggleButton>

            <ToggleButton value="average">Average Price</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {/* )} */}
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, color: "#666" }}>
          Category
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {visibleCategoryColumns.map((category) => (
            <Box
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                // if (category === "average") {
                //   setMode("amount");
                // }
                setSelectedVariable(null);
                if (isMobile) {
                  setMobileFiltersOpen(false);
                }
              }}
              sx={{
                p: 2,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor:
                  selectedCategory === category ? "#1976d2" : "white",
                color: selectedCategory === category ? "white" : "#222",
                border: "1px solid #e0e0e0",
                fontWeight: selectedCategory === category ? 600 : 400,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    selectedCategory === category ? "#1565c0" : "#f5f5f5",
                },
              }}
            >
              {categoryLabels[category]}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  useQuery({
    queryKey: [
      "getCategoryDataSummary",
      selectedCategory,
      selectedYearIds,
      selectedTransactionTypeIds,
      effectiveCoAgentIds,
    ],
    queryFn: () => {
      // Skip API call for aggregate tabs
      if (selectedCategory !== "total") {
        //&& selectedCategory !== "average"
        //
        dispatch(
          getCategoryDataSummary(
            selectedYearIds,
            selectedCategory,
            selectedTransactionTypeIds,
            effectiveCoAgentIds,
          ),
        );
      }
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
    enabled: selectedCategory !== undefined && selectedCategory !== "total",
    //selectedCategory !== "average",
  });

  return (
    <PageLayout>
      <LoadingDialog open={loading} message="Loading Data..." />

      {/* Desktop: Sticky Header Layout */}
      {!isMobile && (
        <>
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 3, ml: 1 }}
          >
            Analytics Overview
          </Typography>

          {/* Sticky Filter Section */}
          <Box
            sx={{
              position: "sticky",
              top: 0, // Change back to 0 since we're now in the scrollable container
              zIndex: 1000,
              backgroundColor: "white",
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              pb: 3,
              mb: 4,
              pt: 2,
              borderRadius: "0 0 12px 12px",
              mx: 1, // Add horizontal margin to match the title
            }}
          >
            {/* Filter Controls Row */}
            <Box
              sx={{
                mb: 3,
                p: 3,
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: "#495057",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <FilterListIcon sx={{ fontSize: 20 }} />
                Filters
              </Typography>

              <Grid2 container spacing={3} alignItems="center">
                <Grid2 size={3}>
                  <YearFilterSelect
                    years={years}
                    selectedYearIds={selectedYearIds}
                    onChange={(value) => {
                      setSelectedYearIds(value);
                      if (value.length === 1) {
                        setTimePeriod("quarter");
                      }
                    }}
                    label="Year"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                  />
                </Grid2>

                <Grid2 size={3}>
                  <TransactionTypeFilterSelect
                    transactionTypes={transactionTypes || []}
                    selectedTransactionTypeIds={selectedTransactionTypeIds}
                    onChange={(value) => {
                      setSelectedTransactionTypeIds(value);
                    }}
                    label="Transaction Type"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                  />
                </Grid2>

                {/* {selectedCategory !== "average" && ( */}
                <Grid2 size={3}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, color: "#6c757d" }}
                  >
                    Data Analyzed
                  </Typography>
                  <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, newMode) => newMode && setMode(newMode)}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        px: 2,
                        py: 0.5,
                        borderColor: "#dee2e6",
                        color: "#6c757d",
                        "&.Mui-selected": {
                          backgroundColor: "#1976d2",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="count">Units</ToggleButton>
                    <ToggleButton value="amount">Volume</ToggleButton>

                    <ToggleButton value="average">Average Price</ToggleButton>
                  </ToggleButtonGroup>
                </Grid2>
                {/* )} */}

                {(selectedYearIds.length === 1 || true) && (
                  <Grid2 size={3}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontWeight: 500, color: "#6c757d" }}
                    >
                      View Mode
                    </Typography>
                    <ToggleButtonGroup
                      value={timePeriod}
                      exclusive
                      onChange={(_, newTimePeriod) =>
                        newTimePeriod && setTimePeriod(newTimePeriod)
                      }
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          px: 2,
                          py: 0.5,
                          borderColor: "#dee2e6",
                          color: "#6c757d",
                          "&.Mui-selected": {
                            backgroundColor: "#1976d2",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "#1565c0",
                            },
                          },
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                          },
                        },
                      }}
                    >
                      {(selectedYearIds.length > 1 ||
                        selectedYearIds.length === 0) && (
                        <ToggleButton value="year">Yearly</ToggleButton>
                      )}
                      <ToggleButton value="quarter">Quarterly</ToggleButton>
                      <ToggleButton value="month">Monthly</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid2>
                )}

                {/* Page-level Co-Agent Filter (hidden when global filter is active or coAgent category is hidden) */}
                {!isGlobalFilterActive &&
                  !hiddenFields?.some((field) => field.name === "coAgent") && (
                    <Grid2 size={3}>
                      <CoAgentFilterSelect
                        coAgents={coAgents}
                        selectedCoAgentIds={effectiveCoAgentIds}
                        onChange={setPageCoAgentIds}
                        label="Filter by Team Members"
                        size="medium"
                        sx={{
                          backgroundColor: "white",
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Grid2>
                  )}
              </Grid2>
            </Box>

            {/* Category Tabs */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                borderRadius: "12px",
                p: 2,
                border: "1px solid #dee2e6",
              }}
            >
              <Tabs
                value={selectedCategory}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 48,
                  "& .MuiTabs-flexContainer": { gap: 1 },
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    minHeight: 44,
                    minWidth: 100,
                    borderRadius: "8px",
                    color: "#495057",
                    background: "white",
                    border: "1px solid #dee2e6",
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                      borderColor: "#1976d2",
                    },
                    "&.Mui-selected": {
                      background: "#1976d2",
                      color: "white",
                      border: "1px solid #1976d2",
                      boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)",
                    },
                  },
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTabs-scrollButtons": {
                    color: "#1976d2",
                    "&.Mui-disabled": {
                      opacity: 0.3,
                    },
                  },
                }}
              >
                {visibleCategoryColumns.map((category) => (
                  <Tab
                    label={categoryLabels[category]}
                    value={category}
                    key={category}
                    disableRipple
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
        </>
      )}

      {isMobile && (
        <>
          <Fab
            color="primary"
            sx={{
              position: "fixed",
              bottom: 24,
              left: 24,
              zIndex: 1300,
              boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
              "&:hover": {
                boxShadow: "0 8px 25px rgba(25, 118, 210, 0.5)",
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
            onClick={() => setMobileFiltersOpen(true)}
          >
            <Badge
              color="secondary"
              variant="dot"
              invisible={selectedYearIds.length === 0 && mode === "count"}
            >
              <FilterListIcon />
            </Badge>
          </Fab>

          <Drawer
            anchor="left"
            open={mobileFiltersOpen}
            onClose={() => setMobileFiltersOpen(false)}
            sx={{
              zIndex: 1400,
              "& .MuiDrawer-paper": {
                width: { xs: 320, sm: 360 },
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="h6" color="primary">
                Filters
              </Typography>
              <IconButton onClick={() => setMobileFiltersOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            {FilterContent}
          </Drawer>
        </>
      )}

      {/* Main Content Area */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {isMobile && (
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 4 }}
          >
            Analytics Overview
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {!["total", "average"].includes(selectedCategory) && (
              <Grid item xs={12} md={12} lg={6}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,

                        color: "primary.main",
                      }}
                    >
                      Distribution Overview
                    </Typography>
                    {/* <IconButton
                      onClick={() => setExpandPieChart(true)}
                      size="small"
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <FullscreenIcon sx={{ fontSize: 14 }} />
                    </IconButton> */}
                  </Box>
                  {selectedCategory === "vendors" ? (
                    <BarChartWrapper
                      category={selectedCategory}
                      yearIds={selectedYearIds}
                      mode={mode}
                      onSliceClick={handleSliceClick}
                      yearText={selectedYearText}
                    />
                  ) : (
                    <PieChartWrapper
                      category={
                        selectedCategory === "total" //|| selectedCategory === "average"
                          ? "type"
                          : selectedCategory
                      }
                      yearIds={selectedYearIds}
                      mode={mode}
                      onSliceClick={handleSliceClick}
                      yearText={selectedYearText}
                    />
                  )}
                </Box>
              </Grid>
            )}

            {!["total", "average"].includes(selectedCategory) && (
              <Grid item xs={12} md={12} lg={6}>
                {selectedVariable ? (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                        }}
                      >
                        Trend Analysis for {selectedVariable.value}
                      </Typography>
                      <IconButton
                        onClick={() => setExpandLineChart(true)}
                        size="small"
                        aria-label="Expand"
                        sx={{
                          // backgroundColor: "gray",
                          // color: "white",
                          // "&:hover": {
                          //   backgroundColor: "primary.dark",
                          // },
                          scale: 1.5,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <FullscreenIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    <LineChartWrapper
                      category={selectedCategory}
                      variableId={selectedVariable.id}
                      selectedYearIds={selectedYearIds}
                      mode={mode}
                      color={selectedVariable.color}
                      yearText={selectedYearText}
                      timePeriod={timePeriod}
                      expandTrigger={expandLineChart}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#333",
                        }}
                      >
                        Trend Analysis for All Variables
                      </Typography>
                      <IconButton
                        onClick={() => setExpandAllVariablesChart(true)}
                        size="small"
                        aria-label="Expand"
                        sx={{
                          //color: "white",
                          "&:hover": {
                            //backgroundColor: "primary.dark",
                          },
                          px: 1,
                          py: 0.5,
                          scale: 1.5,
                        }}
                      >
                        <FullscreenIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    <AllVariablesTrendChart
                      category={selectedCategory}
                      selectedYearIds={selectedYearIds}
                      mode={mode}
                      timePeriod={timePeriod}
                      expandTrigger={expandAllVariablesChart}
                    />
                  </Box>
                )}
              </Grid>
            )}

            {["total", "average"].includes(selectedCategory) && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    {selectedCategory === "total"
                      ? "Total Trend"
                      : "Average Trend"}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This analysis <b>always</b> accounts for Seller and Buyer
                    transactions. Check the Type tab for other transaction
                    types.
                  </Alert>
                  <AggregateTrendChart
                    aggregationType={
                      mode === "average" //|| selectedCategory === "average"
                        ? AggregationTypesEnum.Average
                        : AggregationTypesEnum.Total
                    }
                    mode={mode}
                    selectedYearIds={selectedYearIds}
                    timePeriod={timePeriod}
                    selectedTypeIds={[]}
                    coAgentIds={effectiveCoAgentIds}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default Reports;
